import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import GoogleMapView, { useGoogleMapsLoader } from '@/components/GoogleMapView';
import { GoogleMap, Marker, Autocomplete } from '@react-google-maps/api';
import confetti from 'canvas-confetti';
import { 
  Package, Truck, CheckCircle, Clock, MapPin, Phone, 
  MessageCircle, ExternalLink, AlertCircle, Star, ChevronRight, ChevronLeft,
  DollarSign, CreditCard, X, Loader2, ShoppingBag, User, PackageCheck, Volume2, VolumeX, Hammer
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Success sound URL - premium notification sound
const SUCCESS_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3';

// Inline SVG marker icons (data URLs) — refined driver (truck on blue
// pill with glow) and destination (red home-pin with shadow).
const DRIVER_MARKER_ICON =
  'data:image/svg+xml;charset=UTF-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 56 56" width="48" height="48">' +
      '<defs><filter id="s" x="-30%" y="-30%" width="160%" height="160%"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#1e3a8a" flood-opacity="0.55"/></filter></defs>' +
      '<circle cx="28" cy="28" r="22" fill="#3B82F6" stroke="#ffffff" stroke-width="3" filter="url(#s)"/>' +
      '<circle cx="28" cy="28" r="26" fill="none" stroke="#3B82F6" stroke-opacity="0.35" stroke-width="2"/>' +
      '<path d="M16 32V22h14v10h2v-6l4 2v4h2c1.1 0 2 .9 2 2v3h-3c0 1.66-1.34 3-3 3s-3-1.34-3-3h-8c0 1.66-1.34 3-3 3s-3-1.34-3-3h-2c-1.1 0-2-.9-2-2v-3h3z" fill="#ffffff"/>' +
      '</svg>'
  );

const DEST_MARKER_ICON =
  'data:image/svg+xml;charset=UTF-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 56" width="40" height="48">' +
      '<defs><filter id="ds" x="-30%" y="-30%" width="160%" height="160%"><feDropShadow dx="0" dy="3" stdDeviation="2" flood-color="#7f1d1d" flood-opacity="0.55"/></filter></defs>' +
      '<path d="M24 2C13.5 2 5 10.5 5 21c0 13.5 19 33 19 33s19-19.5 19-33C43 10.5 34.5 2 24 2z" fill="#EF4444" stroke="#ffffff" stroke-width="2.5" filter="url(#ds)"/>' +
      '<path d="M24 12l-9 7v10h6v-6h6v6h6V19l-9-7z" fill="#ffffff"/>' +
      '</svg>'
  );

const OrderTrackingPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [tipAmount, setTipAmount] = useState(null);
  const [customTip, setCustomTip] = useState('');
  const [showTipModal, setShowTipModal] = useState(false);
  const [processingTip, setProcessingTip] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(null);
  const [soundMuted, setSoundMuted] = useState(() => {
    return sessionStorage.getItem('celebration_sound_muted') === 'true';
  });
  const [showMuteButton, setShowMuteButton] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [pickerCoords, setPickerCoords] = useState(null); // { lat, lng }
  const [pickerAddress, setPickerAddress] = useState('');
  const [pickerLoadingAddress, setPickerLoadingAddress] = useState(false);
  const [savingPickerLocation, setSavingPickerLocation] = useState(false);
  const { isLoaded: mapsLoaded } = useGoogleMapsLoader();
  const autocompleteRef = useRef(null);
  const audioRef = useRef(null);
  const celebrationTriggeredRef = useRef(false);
  
  // Premium confetti animation
  const triggerConfetti = useCallback(() => {
    const duration = 2500;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }

      const particleCount = 50 * (timeLeft / duration);
      
      // Burst from both sides
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7']
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7']
      });
    }, 250);
  }, []);
  
  // Play success sound
  const playSuccessSound = useCallback(() => {
    if (soundMuted) return;
    
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(SUCCESS_SOUND_URL);
        audioRef.current.volume = 0.5; // 50% volume for subtle effect
      }
      audioRef.current.play().catch(() => {
        // Browser blocked autoplay - that's okay
        console.log('Sound autoplay blocked by browser');
      });
    } catch (err) {
      console.log('Could not play sound:', err);
    }
  }, [soundMuted]);
  
  // Trigger celebration for new orders
  const triggerCelebration = useCallback((orderStatus, celebrationEnabled) => {
    // Only trigger for new orders (pending/unfulfilled)
    const isNewOrder = ['pending', 'unfulfilled', 'confirmed'].includes(orderStatus);
    
    // Check if already triggered this session
    const sessionKey = `celebration_shown_${token}`;
    const alreadyShown = sessionStorage.getItem(sessionKey);
    
    if (isNewOrder && celebrationEnabled && !alreadyShown && !celebrationTriggeredRef.current) {
      celebrationTriggeredRef.current = true;
      sessionStorage.setItem(sessionKey, 'true');
      
      // Trigger confetti
      setTimeout(() => {
        triggerConfetti();
      }, 500);
      
      // Play sound
      setTimeout(() => {
        playSuccessSound();
        setShowMuteButton(true);
        // Hide mute button after 5 seconds
        setTimeout(() => setShowMuteButton(false), 5000);
      }, 300);
    }
  }, [token, triggerConfetti, playSuccessSound]);
  
  // Toggle mute
  const toggleMute = () => {
    const newMuted = !soundMuted;
    setSoundMuted(newMuted);
    sessionStorage.setItem('celebration_sound_muted', newMuted.toString());
    if (audioRef.current && newMuted) {
      audioRef.current.pause();
    }
  };
  
  const fetchTrackingData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/tracking/public/${token}`);
      setTrackingData(response.data);
      setError(null);
      
      // Trigger celebration if enabled
      const orderStatus = response.data?.order?.status;
      const celebrationEnabled = response.data?.celebration?.enabled !== false;
      triggerCelebration(orderStatus, celebrationEnabled);
      
      // Initialize timer if enabled
      if (response.data?.crosssell_timer?.enabled) {
        const sessionKey = `crosssell_timer_${token}`;
        const storedExpiry = sessionStorage.getItem(sessionKey);
        const durationMinutes = response.data.crosssell_timer.duration_minutes || 30;
        
        if (storedExpiry) {
          const remaining = Math.floor((parseInt(storedExpiry) - Date.now()) / 1000);
          if (remaining > 0) {
            setTimerSeconds(remaining);
          } else {
            // Timer expired, reset for new session
            const newExpiry = Date.now() + (durationMinutes * 60 * 1000);
            sessionStorage.setItem(sessionKey, newExpiry.toString());
            setTimerSeconds(durationMinutes * 60);
          }
        } else {
          // First visit, set timer
          const expiry = Date.now() + (durationMinutes * 60 * 1000);
          sessionStorage.setItem(sessionKey, expiry.toString());
          setTimerSeconds(durationMinutes * 60);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Order not found');
    } finally {
      setLoading(false);
    }
  }, [token, triggerCelebration]);
  
  // Timer countdown effect
  useEffect(() => {
    if (timerSeconds === null || timerSeconds <= 0) return;
    
    const interval = setInterval(() => {
      setTimerSeconds(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [timerSeconds]);
  
  // Handle tip success/cancelled from Stripe redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    if (urlParams.get('tip_success') === 'true') {
      toast.success('Thank you for your tip! 🎉 Your generosity is appreciated.');
      // Clean up URL
      window.history.replaceState({}, '', `/track/${token}`);
    } else if (urlParams.get('tip_cancelled') === 'true') {
      toast.info('Tip payment was cancelled.');
      // Clean up URL  
      window.history.replaceState({}, '', `/track/${token}`);
    }
  }, [token]);
  
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  useEffect(() => {
    fetchTrackingData();
    
    // Poll for updates every 30 seconds if order is in transit
    const interval = setInterval(() => {
      if (trackingData?.order?.status === 'on_the_way') {
        fetchTrackingData();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchTrackingData, trackingData?.order?.status]);
  
  const handleTip = async (amount) => {
    if (!amount || amount <= 0) {
      toast.error('Please select or enter a tip amount');
      return;
    }
    
    setProcessingTip(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/tracking/public/${token}/tip?amount=${amount}`
      );
      
      if (response.data.error) {
        toast.error(response.data.error);
        setProcessingTip(false);
        return;
      }
      
      // If Stripe checkout URL is returned, redirect to Stripe
      if (response.data.checkout_url) {
        toast.success('Redirecting to payment...');
        window.location.href = response.data.checkout_url;
        return;
      }
      
      // Legacy: If client_secret is returned (for Stripe Elements)
      if (response.data.client_secret && trackingData?.tip?.stripe_publishable_key) {
        const stripe = window.Stripe?.(trackingData.tip.stripe_publishable_key);
        if (stripe) {
          const { error } = await stripe.confirmCardPayment(response.data.client_secret, {
            payment_method: {
              card: { /* card element would go here */ },
            }
          });
          
          if (error) {
            toast.error(error.message);
          } else {
            await axios.post(`${API_BASE_URL}/tracking/public/${token}/tip/${response.data.tip_id}/confirm`);
            toast.success('Thank you for your tip!');
            setTipAmount(null);
            setCustomTip('');
          }
        }
      } else {
        toast.error('Payment processing unavailable. Please try again later.');
      }
    } catch (err) {
      console.error('Tip error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to process tip. Please try again.';
      toast.error(errorMessage);
    } finally {
      setProcessingTip(false);
    }
  };
  
  // Detect WhatsApp / Instagram / Facebook / LinkedIn / TikTok in-app
  // browsers. Their WebView silently blocks geolocation and Places
  // Autocomplete prompts — the customer gets a "permission denied"
  // toast with NO prompt to allow. We surface a clear nudge to open
  // the link in the real browser instead.
  const isInAppBrowser = (() => {
    if (typeof navigator === 'undefined') return false;
    const ua = (navigator.userAgent || '').toLowerCase();
    return /(fb_iab|fban|fbav|instagram|wv|whatsapp|wabusinessios|wabusiness|linkedinapp|tiktok|line)/i.test(ua);
  })();

  // Handle location sharing from customer
  // Step 1 of the picker: grab the device GPS to seed the draggable pin.
  // The actual save happens in `confirmPickerLocation` (after the user
  // has had a chance to drag the pin to the precise gate/building).
  const handleShareLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGettingLocation(false);
        const coords = { lat: position.coords.latitude, lng: position.coords.longitude };
        setPickerCoords(coords);
        // Reverse-geocode through the backend so we don't ship the
        // Maps key out via JS API just for an address string.
        fetchPickerAddress(coords);
      },
      (error) => {
        setGettingLocation(false);
        // Universal fallback: any time geolocation fails, auto-open
        // the manual pin-drop view at the order's existing delivery
        // location (or Dubai centre) so the customer has a working
        // path right there instead of a dead-end toast.
        const dropPinFallback = () => {
          const fallback = trackingData?.destination_coords?.latitude
            ? { lat: trackingData.destination_coords.latitude,
                lng: trackingData.destination_coords.longitude }
            : { lat: 25.2048, lng: 55.2708 };
          setPickerCoords(fallback);
          fetchPickerAddress(fallback);
        };
        switch (error.code) {
          case error.PERMISSION_DENIED:
            // Two distinct cases:
            //   a) In-app browser (WA/IG/FB) silently blocks geo —
            //      OS settings won't help, only opening the URL in
            //      Safari/Chrome will.
            //   b) Real browser where the user (or iOS settings) has
            //      blocked location for this site. Most common on
            //      iOS Chrome where the prompt has already been
            //      dismissed once and Chrome won't re-ask.
            if (isInAppBrowser) {
              toast.error(
                'Your in-app browser blocks location access. Tap ⋯ menu → "Open in Safari/Chrome" — or drop the pin manually on the map.',
                { duration: 10000 }
              );
            } else {
              toast.error(
                'Location access is blocked. On iPhone: Settings → Chrome → Location → "Ask" or "While Using App". Or just drop the pin manually on the map below.',
                { duration: 10000 }
              );
            }
            // Auto-open the manual fallback so they can finish the
            // task right now without leaving the page.
            dropPinFallback();
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Location information is unavailable. Drop the pin manually instead.');
            dropPinFallback();
            break;
          case error.TIMEOUT:
            toast.error('Location request timed out. Drop the pin manually or retry.');
            dropPinFallback();
            break;
          default:
            toast.error('Failed to get your location. Drop the pin manually instead.');
            dropPinFallback();
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  };

  const fetchPickerAddress = async ({ lat, lng }) => {
    setPickerLoadingAddress(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/tracking/public/${token}/reverse-geocode`, {
        params: { lat, lng },
      });
      setPickerAddress(res.data?.address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`);
    } catch (e) {
      setPickerAddress(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
    } finally {
      setPickerLoadingAddress(false);
    }
  };

  const confirmPickerLocation = async () => {
    if (!pickerCoords) return;
    setSavingPickerLocation(true);
    try {
      await axios.post(`${API_BASE_URL}/tracking/public/${token}/location`, {
        lat: pickerCoords.lat,
        lng: pickerCoords.lng,
        name: 'Customer Shared Location',
        address: pickerAddress || `${pickerCoords.lat.toFixed(6)}, ${pickerCoords.lng.toFixed(6)}`,
      });
      toast.success('Location shared successfully!');
      setShowLocationPicker(false);
      setPickerCoords(null);
      setPickerAddress('');
      fetchTrackingData();
    } catch (err) {
      toast.error('Failed to share location. Please try again.');
    } finally {
      setSavingPickerLocation(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  
  const formatCurrency = (amount, currency = 'AED') => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };
  
  const getStatusProgress = (status) => {
    const statusOrder = ['pending', 'ready_for_dispatch', 'accepted', 'picked', 'on_the_way', 'delivered'];
    const index = statusOrder.indexOf(status);
    return Math.max(0, ((index + 1) / statusOrder.length) * 100);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading order details...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Order Not Found</h1>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }
  
  const { order, timeline, branding, announcement, driver, driver_location, destination_coords, location_source, crosssell_products, crosssell_headline, crosssell_timer, tip, modules, social, cashback, return_info } = trackingData || {};
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      {/* Sound Mute Button - Floating */}
      {showMuteButton && (
        <button
          onClick={toggleMute}
          className="fixed top-4 right-4 z-50 p-2 bg-slate-800/90 backdrop-blur-sm rounded-full border border-slate-700 shadow-lg transition-all hover:scale-110"
          title={soundMuted ? 'Unmute sound' : 'Mute sound'}
        >
          {soundMuted ? (
            <VolumeX className="w-5 h-5 text-gray-400" />
          ) : (
            <Volume2 className="w-5 h-5 text-green-400" />
          )}
        </button>
      )}
      
      {/* Announcement Bar */}
      {announcement?.enabled && announcement?.text && (
        <div 
          className="w-full py-2 px-4 text-center text-sm"
          style={{ 
            backgroundColor: announcement.bg_color || '#1a1a2e',
            color: announcement.text_color || '#ffffff'
          }}
        >
          {announcement.link ? (
            <a 
              href={announcement.link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:underline flex items-center justify-center gap-2"
            >
              {announcement.text}
              <ExternalLink className="w-3 h-3" />
            </a>
          ) : (
            announcement.text
          )}
        </div>
      )}
      
      {/* Header with Branding - Centered Logo */}
      <header className="bg-slate-800/50 border-b border-slate-700 p-4">
        <div className="max-w-lg mx-auto flex items-center justify-center">
          {branding?.logo_url ? (
            <a 
              href={branding.logo_redirect_url || '#'} 
              target={branding.logo_redirect_url ? '_blank' : '_self'}
              rel="noopener noreferrer"
              className="flex justify-center"
            >
              <img 
                src={branding.logo_url} 
                alt={branding.brand_name || 'Logo'} 
                className="h-10 w-auto object-contain"
              />
            </a>
          ) : branding?.brand_name ? (
            <h1 className="text-xl font-bold text-white text-center">{branding.brand_name}</h1>
          ) : (
            <h1 className="text-xl font-bold text-white text-center">Order Tracking</h1>
          )}
        </div>
      </header>
      
      <main className="max-w-lg mx-auto p-4 space-y-6">
        {/* Cashback Banner */}
        {cashback && (cashback.credited ? (
          <div className="rounded-2xl p-4 bg-gradient-to-r from-emerald-600/25 to-teal-600/25 border border-emerald-500/40" data-testid="cashback-credited-banner">
            <p className="text-emerald-300 font-bold text-lg">🎉 {cashback.credited.amount} {cashback.currency} cashback added!</p>
            <p className="text-sm text-gray-300 mt-1">
              It's in your wallet{typeof cashback.wallet_balance === 'number' ? ` — balance ${cashback.wallet_balance} ${cashback.currency}` : ''}. Treat yourself on your next order! 🛍️
            </p>
          </div>
        ) : (
          <div className="rounded-2xl p-4 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/40" data-testid="cashback-promise-banner">
            <p className="text-amber-300 font-bold text-lg">💰 Earn {cashback.expected} {cashback.currency} cashback on this order</p>
            <p className="text-sm text-gray-300 mt-1">{cashback.percentage}% cashback lands in your wallet once your order is delivered.</p>
          </div>
        ))}

        {/* Order Summary Card */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-gray-400 text-sm">Order Number</p>
              <p className="text-white font-semibold text-lg">{order?.order_number}</p>
            </div>
            <Badge 
              className={`${
                order?.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                order?.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                order?.status === 'on_the_way' ? 'bg-blue-500/20 text-blue-400' :
                'bg-yellow-500/20 text-yellow-400'
              } border-0 px-3 py-1`}
            >
              {order?.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </Badge>
          </div>
          
          {/* Progress Bar */}
          {order?.status !== 'failed' && (
            <div className="mb-4">
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500"
                  style={{ width: `${getStatusProgress(order?.status)}%` }}
                />
              </div>
            </div>
          )}
          
          {/* Order Total */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">Total Amount</span>
            <span className="text-white font-semibold">{formatCurrency(order?.total_amount, order?.currency)}</span>
          </div>
          
          {/* Courier Information */}
          {order?.courier && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-blue-400" />
                  <span className="text-gray-400 text-sm">Shipped via</span>
                  <span className="text-white font-medium">{order.courier.name}</span>
                </div>
                {order.courier.tracking_url && (
                  <a
                    href={order.courier.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm"
                  >
                    Track <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              {order.courier.awb && (
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  AWB: {order.courier.awb}
                </p>
              )}
            </div>
          )}
        </div>
        
        {/* Live Map - Always visible when map module enabled */}
        {modules?.map && (
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
            <div className="p-3 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-400" />
                {driver_location ? 'Live Tracking' : 'Delivery Location'}
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowLocationPicker(true)}
                className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20 text-xs"
                data-testid="add-update-location-btn"
              >
                <MapPin className="w-3 h-3 mr-1" />
                {destination_coords ? 'Update Location' : 'Add Location Pin'}
              </Button>
            </div>
            <div className="h-64">
              {(driver_location || destination_coords) ? (
                <GoogleMapView
                  height="100%"
                  zoom={15}
                  testId="tracking-map"
                  center={
                    driver_location
                      ? { lat: driver_location.latitude, lng: driver_location.longitude }
                      : destination_coords
                        ? { lat: destination_coords.latitude, lng: destination_coords.longitude }
                        : { lat: 25.2048, lng: 55.2708 }
                  }
                  follow={
                    driver_location
                      ? { lat: driver_location.latitude, lng: driver_location.longitude }
                      : null
                  }
                  fitToMarkers={Boolean(driver_location && destination_coords)}
                  markers={[
                    driver_location && {
                      id: 'driver',
                      position: { lat: driver_location.latitude, lng: driver_location.longitude },
                      iconUrl: DRIVER_MARKER_ICON,
                      info: `${driver?.name || 'Driver'} — on the way`,
                    },
                    destination_coords && {
                      id: 'destination',
                      position: { lat: destination_coords.latitude, lng: destination_coords.longitude },
                      iconUrl: DEST_MARKER_ICON,
                      info: destination_coords.name || 'Delivery Location',
                    },
                  ].filter(Boolean)}
                />
              ) : (
                <div className="h-full flex flex-col items-center justify-center bg-slate-900/50 text-gray-400">
                  <MapPin className="w-12 h-12 mb-3 opacity-50" />
                  <p className="text-sm">No location pin added yet</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowLocationPicker(true)}
                    className="mt-3 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    Add Your Location
                  </Button>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between p-2 border-t border-slate-700">
              {driver_location?.updated_at && (
                <p className="text-xs text-gray-500">
                  Last updated: {formatDate(driver_location.updated_at)}
                </p>
              )}
              {destination_coords && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${
                  location_source === 'customer_pin' || location_source === 'whatsapp_pin'
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-blue-500/20 text-blue-400'
                }`}>
                  <MapPin className="w-3 h-3" />
                  {location_source === 'customer_pin' || location_source === 'whatsapp_pin' ? 'Customer Shared' : 'Address Estimated'}
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Driver Details */}
        {modules?.driver && driver && (
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Truck className="w-4 h-4 text-blue-400" />
              Your Driver
            </h3>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-slate-700 overflow-hidden flex-shrink-0">
                {driver.photo_url ? (
                  <img src={driver.photo_url} alt={driver.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white text-xl font-semibold">
                    {driver.name?.charAt(0) || 'D'}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold truncate">{driver.name}</p>
                {driver.rating && (
                  <div className="flex items-center gap-1 text-yellow-400 text-sm">
                    <Star className="w-4 h-4 fill-current" />
                    <span>{driver.rating.toFixed(1)}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {driver.phone && (
                  <>
                    <a 
                      href={`tel:${driver.phone}`}
                      className="p-2 bg-blue-500/20 rounded-full text-blue-400 hover:bg-blue-500/30 transition-colors"
                    >
                      <Phone className="w-5 h-5" />
                    </a>
                    <a 
                      href={`https://wa.me/${driver.phone.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-green-500/20 rounded-full text-green-400 hover:bg-green-500/30 transition-colors"
                    >
                      <MessageCircle className="w-5 h-5" />
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Estimated delivery */}
        {order?.delivery_estimate && order?.status !== 'delivered' && (
          <div className={`rounded-xl border p-4 flex items-center gap-3 ${order.delivery_estimate.delayed ? 'bg-amber-500/10 border-amber-500/40' : 'bg-blue-500/10 border-blue-500/40'}`} data-testid="delivery-estimate-banner">
            <Truck className={`w-5 h-5 ${order.delivery_estimate.delayed ? 'text-amber-400' : 'text-blue-400'}`} />
            <div>
              <p className="text-white font-medium text-sm">
                {order.delivery_estimate.basis === 'express' ? 'Express delivery: '
                  : order.delivery_estimate.delayed ? 'Delayed — new estimated delivery: ' : 'Estimated delivery: '}
                {order.delivery_estimate.basis === 'express' && order.delivery_estimate.express_deadline
                  ? new Date(order.delivery_estimate.express_deadline).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                  : <>
                      {new Date(`${order.delivery_estimate.min_date}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      {order.delivery_estimate.max_date !== order.delivery_estimate.min_date && (
                        <> – {new Date(`${order.delivery_estimate.max_date}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</>
                      )}
                    </>}
              </p>
              {order.delivery_estimate.basis === 'express' && (
                <p className="text-xs text-emerald-300 font-medium" data-testid="tracking-express-note">Express order — priority handling</p>
              )}
              {order.delivery_estimate.basis === 'production' && (
                <p className="text-xs text-gray-400">Includes manufacturing time for your custom piece</p>
              )}
            </div>
          </div>
        )}

        {/* Timeline */}
        {modules?.timeline && timeline && timeline.length > 0 && (
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-400" />
              Order Timeline
            </h3>
            <div className="space-y-0">
              {timeline.map((item, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      item.is_failure ? 'bg-red-500/20 text-red-400' :
                      item.completed ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-gray-500'
                    }`}>
                      {item.status === 'order_placed' && <ShoppingBag className="w-4 h-4" />}
                      {item.status === 'processing' && <Clock className="w-4 h-4" />}
                      {item.status === 'in_production' && <Hammer className="w-4 h-4" />}
                      {item.status === 'ready_for_dispatch' && <Package className="w-4 h-4" />}
                      {item.status === 'accepted' && <User className="w-4 h-4" />}
                      {item.status === 'courier_booked' && <Truck className="w-4 h-4" />}
                      {item.status === 'picked' && <PackageCheck className="w-4 h-4" />}
                      {item.status === 'in_transit' && <Truck className="w-4 h-4" />}
                      {item.status === 'out_for_delivery' && <Truck className="w-4 h-4" />}
                      {item.status === 'on_the_way' && <Truck className="w-4 h-4" />}
                      {item.status === 'delivered' && <CheckCircle className="w-4 h-4" />}
                      {item.status === 'failed' && <AlertCircle className="w-4 h-4" />}
                    </div>
                    {index < timeline.length - 1 && (
                      <div className={`w-0.5 h-10 ${item.completed ? 'bg-green-500/50' : 'bg-slate-700'}`} />
                    )}
                  </div>
                  <div className="flex-1 pb-6">
                    <p className={`font-medium ${
                      item.is_failure ? 'text-red-400' : 
                      item.completed ? 'text-white' : 'text-gray-500'
                    }`}>
                      {item.label}
                    </p>
                    {item.timestamp ? (
                      <p className="text-sm text-gray-500">{formatDate(item.timestamp)}</p>
                    ) : (
                      <p className="text-sm text-gray-600">Pending</p>
                    )}
                    {item.status === 'in_production' && order?.production?.estimated_ready_date && !order?.production?.completed_at && (
                      <p className="text-sm text-amber-400 mt-1" data-testid="production-eta-text">
                        Your piece is being handcrafted — expected ready by {new Date(`${order.production.estimated_ready_date}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Order Items */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-blue-400" />
            Order Items
          </h3>
          <div className="space-y-3">
            {order?.items?.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-slate-700 overflow-hidden flex-shrink-0">
                  {item.image_url || item.image ? (
                    <img 
                      src={item.image_url || item.image} 
                      alt={item.title || item.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-6 h-6 text-gray-500" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{item.title || item.name}</p>
                  <p className="text-gray-500 text-xs">Qty: {item.quantity || 1}</p>
                </div>
                <p className="text-white text-sm font-medium">
                  {formatCurrency(item.price, order?.currency)}
                </p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Delivery Address */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
          <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-blue-400" />
            Delivery Address
          </h3>
          <p className="text-gray-400 text-sm">
            {order?.shipping_address && typeof order?.shipping_address === 'object' 
              ? [
                  order.shipping_address.address1,
                  order.shipping_address.address2,
                  order.shipping_address.city,
                  order.shipping_address.province,
                  order.shipping_address.country
                ].filter(Boolean).join(', ')
              : order?.shipping_address
            }
          </p>
        </div>
        
        {/* Tip Section - Show always when enabled */}
        {modules?.tip && tip?.enabled && (
          <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-xl border border-emerald-500/30 p-4">
            <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
              <span className="text-lg">💝</span>
              Leave a Tip
            </h3>
            <p className="text-gray-400 text-sm mb-4">Show appreciation for great service!</p>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {(tip?.slabs || [5, 10, 20, 50]).map((amount) => (
                <Button
                  key={amount}
                  variant={tipAmount === amount ? "default" : "outline"}
                  className={`${tipAmount === amount ? 'bg-emerald-500 text-white' : 'border-emerald-500/50 text-emerald-400'}`}
                  onClick={() => {
                    setTipAmount(amount);
                    setCustomTip('');
                  }}
                >
                  {tip?.currency || 'AED'} {amount}
                </Button>
              ))}
            </div>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">{tip?.currency || 'AED'}</span>
                <input
                  type="number"
                  placeholder="Custom"
                  value={customTip}
                  onChange={(e) => {
                    setCustomTip(e.target.value);
                    setTipAmount(null);
                  }}
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-12 pr-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500"
                />
              </div>
              <Button
                onClick={() => handleTip(parseFloat(customTip) || tipAmount)}
                disabled={(!tipAmount && !customTip) || processingTip}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-6"
              >
                {processingTip ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Tip'}
              </Button>
            </div>
          </div>
        )}
        
        {/* Cross-sell Products - Modern Carousel */}
        {modules?.crosssell && crosssell_products && crosssell_products.length > 0 && (
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-2xl border border-slate-700/50 p-5 overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-pink-400" />
                {crosssell_headline || 'You Might Also Like'}
              </h3>
              {crosssell_timer?.enabled && timerSeconds > 0 && (
                <div className="flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-orange-500/30 to-red-500/30 rounded-full border border-orange-500/50">
                  <Clock className="w-4 h-4 text-orange-400 animate-pulse" />
                  <span className="text-orange-300 font-mono text-sm font-bold">
                    {formatTimer(timerSeconds)}
                  </span>
                </div>
              )}
            </div>
            {crosssell_timer?.enabled && timerSeconds > 0 && (
              <p className="text-sm text-orange-300/80 mb-4">🔥 Limited time offer! Grab these deals now.</p>
            )}
            
            {/* Product Carousel */}
            <div className="relative">
              <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {crosssell_products.map((product, index) => {
                  // Build product URL - prefer handle, fallback to Shopify product ID
                  const baseUrl = branding?.logo_redirect_url || branding?.store_url || '';
                  let productUrl = '';
                  
                  if (product.handle && product.handle.trim()) {
                    productUrl = `${baseUrl}/products/${product.handle}`;
                  } else if (product.shopify_product_id) {
                    productUrl = `${baseUrl}/products/${product.shopify_product_id}`;
                  } else if (baseUrl) {
                    // Fallback to store URL if no specific product link
                    productUrl = baseUrl;
                  }
                  
                  const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;
                  const discountPercent = hasDiscount 
                    ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
                    : 0;
                  
                  return (
                    <div
                      key={index}
                      className="flex-shrink-0 w-44 snap-start bg-slate-800/60 rounded-xl border border-slate-700/50 overflow-hidden group hover:border-pink-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-pink-500/10"
                    >
                      {/* Product Image */}
                      <div className="relative aspect-square bg-slate-700 overflow-hidden">
                        {product.image_url ? (
                          <img 
                            src={product.image_url} 
                            alt={product.title} 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-12 h-12 text-gray-600" />
                          </div>
                        )}
                        {hasDiscount && (
                          <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-md">
                            -{discountPercent}%
                          </div>
                        )}
                      </div>
                      
                      {/* Product Details */}
                      <div className="p-3">
                        <p className="text-white text-sm font-medium line-clamp-2 h-10 mb-2">{product.title}</p>
                        
                        {/* Pricing */}
                        <div className="flex items-center gap-2 mb-3">
                          {hasDiscount && (
                            <span className="text-gray-500 text-xs line-through">
                              {formatCurrency(product.compare_at_price, order?.currency)}
                            </span>
                          )}
                          <span className={`font-bold ${hasDiscount ? 'text-green-400' : 'text-white'}`}>
                            {formatCurrency(product.price, order?.currency)}
                          </span>
                        </div>
                        
                        {/* Buy Now Button */}
                        {productUrl ? (
                          <a
                            href={productUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full text-center bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white text-sm font-semibold py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02]"
                          >
                            Buy Now
                          </a>
                        ) : (
                          <span className="block w-full text-center bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm font-semibold py-2 px-4 rounded-lg opacity-75">
                            View Product
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Scroll Indicators - Gradient fades */}
              {crosssell_products.length > 2 && (
                <>
                  <div className="absolute left-0 top-0 bottom-2 w-8 bg-gradient-to-r from-slate-800/80 to-transparent pointer-events-none" />
                  <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-slate-800/80 to-transparent pointer-events-none" />
                </>
              )}
            </div>
            
            {/* Scroll hint for mobile */}
            {crosssell_products.length > 2 && (
              <p className="text-center text-xs text-gray-500 mt-3">
                <ChevronLeft className="w-3 h-3 inline" /> Swipe to see more <ChevronRight className="w-3 h-3 inline" />
              </p>
            )}
          </div>
        )}
        
        {/* Returns & Refunds status */}
        {return_info && (
          <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-5" data-testid="return-status-section">
            <h3 className="text-white font-semibold">Returns & Refunds</h3>
            <p className="text-xs text-gray-400 mb-4">Return {return_info.return_number}</p>
            {['cancelled', 'rejected'].includes(return_info.status) ? (
              <p className="text-sm text-gray-300">
                This return was {return_info.status}.{return_info.cancel_reason ? ` Reason: ${return_info.cancel_reason}` : ''}
              </p>
            ) : (
              <div className="space-y-2.5">
                {['Return requested', 'Approved', 'Picked up', 'Received at warehouse', 'Refund processed'].map((label, i) => (
                  <div key={label} className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${i <= return_info.step ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-gray-500'}`}>
                      {i <= return_info.step ? '✓' : i + 1}
                    </div>
                    <span className={`text-sm ${i <= return_info.step ? 'text-white' : 'text-gray-500'}`}>{label}</span>
                  </div>
                ))}
                {return_info.refund_amount > 0 && return_info.step >= 4 && (
                  <p className="text-sm text-emerald-400 pt-1" data-testid="refund-processed-note">
                    Refund of {return_info.refund_amount} {order?.currency || 'AED'} processed ✓
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Follow us — social links */}
        {social?.enabled && Object.values(social.links || {}).some(Boolean) && (
          <div className="text-center py-2" data-testid="social-links">
            <p className="text-gray-300 font-medium text-sm mb-3">Follow us for offers & new arrivals</p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              {[
                ['instagram', 'M12 2.2c3.2 0 3.6 0 4.8.1 3.3.1 4.8 1.7 4.9 4.9.1 1.3.1 1.6.1 4.8s0 3.6-.1 4.8c-.1 3.2-1.7 4.8-4.9 4.9-1.3.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-3.3-.1-4.8-1.7-4.9-4.9-.1-1.3-.1-1.6-.1-4.8s0-3.6.1-4.8C2.4 4 4 2.4 7.2 2.3 8.4 2.2 8.8 2.2 12 2.2zm0 3.6a6.2 6.2 0 100 12.4 6.2 6.2 0 000-12.4zm0 2.2a4 4 0 110 8 4 4 0 010-8zm6.4-3.8a1.44 1.44 0 100 2.9 1.44 1.44 0 000-2.9z', 'hover:bg-pink-600/30 hover:border-pink-500'],
                ['facebook', 'M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.04V9.41c0-3.02 1.8-4.7 4.54-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.5c-1.5 0-1.96.93-1.96 1.89v2.26h3.32l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z', 'hover:bg-blue-600/30 hover:border-blue-500'],
                ['tiktok', 'M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z', 'hover:bg-slate-600/40 hover:border-slate-400'],
                ['whatsapp', 'M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.7.63.71.23 1.36.19 1.87.12.57-.09 1.76-.72 2-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35m-5.42 7.4h-.01a9.87 9.87 0 01-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 01-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 012.89 6.99c0 5.45-4.44 9.88-9.89 9.88m8.41-18.29A11.82 11.82 0 0012.05 0C5.5 0 .16 5.33.16 11.89c0 2.1.55 4.14 1.59 5.94L.06 24l6.33-1.66a11.88 11.88 0 005.66 1.44h.01c6.55 0 11.89-5.33 11.89-11.89 0-3.18-1.24-6.16-3.49-8.4', 'hover:bg-green-600/30 hover:border-green-500'],
                ['twitter', 'M18.24 2.25h3.31l-7.23 8.26L22.83 21.75h-6.66l-5.22-6.82-5.97 6.82H1.67l7.73-8.84L1.25 2.25h6.83l4.71 6.23zm-1.16 17.52h1.83L7.08 4.13H5.12z', 'hover:bg-slate-600/40 hover:border-slate-400'],
                ['youtube', 'M23.5 6.19a3.02 3.02 0 00-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.5A3.02 3.02 0 00.5 6.19C0 8.07 0 12 0 12s0 3.93.5 5.81a3.02 3.02 0 002.12 2.14c1.88.5 9.38.5 9.38.5s7.5 0 9.38-.5a3.02 3.02 0 002.12-2.14C24 15.93 24 12 24 12s0-3.93-.5-5.81zM9.55 15.57V8.43L15.82 12z', 'hover:bg-red-600/30 hover:border-red-500'],
                ['pinterest', 'M12 0C5.37 0 0 5.37 0 12c0 5.08 3.16 9.43 7.63 11.17-.11-.95-.2-2.4.04-3.44.22-.93 1.4-5.94 1.4-5.94s-.36-.71-.36-1.77c0-1.66.96-2.9 2.16-2.9 1.02 0 1.51.76 1.51 1.68 0 1.02-.65 2.56-.99 3.98-.28 1.19.6 2.16 1.77 2.16 2.12 0 3.76-2.24 3.76-5.47 0-2.86-2.06-4.86-5-4.86-3.4 0-5.4 2.55-5.4 5.19 0 1.03.4 2.13.89 2.73.1.12.11.22.08.34l-.33 1.36c-.05.22-.17.27-.4.16-1.5-.7-2.43-2.89-2.43-4.65 0-3.78 2.75-7.26 7.93-7.26 4.16 0 7.4 2.97 7.4 6.93 0 4.14-2.61 7.47-6.23 7.47-1.22 0-2.36-.63-2.75-1.38l-.75 2.85c-.27 1.04-1 2.35-1.49 3.15 1.12.35 2.31.53 3.56.53 6.63 0 12-5.37 12-12S18.63 0 12 0z', 'hover:bg-red-700/30 hover:border-red-600'],
              ].map(([key, path, hover]) => {
                let url = social.links[key];
                if (!url) return null;
                if (key === 'whatsapp' && !/^https?:/i.test(url)) {
                  url = `https://wa.me/${String(url).replace(/[^\d]/g, '')}`;
                } else if (!/^https?:/i.test(url)) {
                  url = `https://${url}`;
                }
                return (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-11 h-11 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-gray-300 transition-all hover:scale-110 hover:text-white ${hover}`}
                    data-testid={`social-${key}`}
                    aria-label={key}
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d={path} /></svg>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="text-center text-gray-500 text-xs py-4">
          <p>
            Powered by{' '}
            <a
              href="https://opswarex.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-amber-400 underline-offset-2 hover:underline transition-colors"
              data-testid="powered-by-opswarex-link"
            >
              Opswarex
            </a>
          </p>
        </footer>
      </main>
      
      {/* Location Picker Modal */}
      {showLocationPicker && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-slate-800 w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl border border-slate-700 max-h-[92vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-slate-800/95 backdrop-blur px-5 py-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-base font-semibold text-white flex items-center gap-2">
                <MapPin className="w-5 h-5 text-emerald-400" />
                Share Delivery Location
              </h3>
              <button
                onClick={() => {
                  setShowLocationPicker(false);
                  setPickerCoords(null);
                  setPickerAddress('');
                }}
                className="text-gray-400 hover:text-white p-1"
                data-testid="location-picker-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4 space-y-4">
              {/* In-app browser warning — geolocation + Places API often
                  silently fail inside WhatsApp/Instagram/FB WebViews.
                  Surface it BEFORE the user hits a wall. */}
              {isInAppBrowser && (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-[12px] text-amber-200 flex items-start gap-2"
                     data-testid="inapp-browser-warning">
                  <span className="text-base leading-none">⚠️</span>
                  <div>
                    <div className="font-semibold mb-0.5">Open this page in your full browser</div>
                    <div className="opacity-90">
                      The in-app browser may block location access and address search. Tap the ⋯ menu (or Share icon) and choose <b>"Open in Safari"</b> or <b>"Open in Chrome"</b> for the best experience.
                    </div>
                    <button
                      onClick={() => {
                        try {
                          navigator.clipboard.writeText(window.location.href);
                          toast.success('Link copied — paste it in Safari or Chrome');
                        } catch {
                          toast.info('Long-press the URL bar above to copy the link.');
                        }
                      }}
                      className="mt-2 text-[11px] underline underline-offset-2"
                      data-testid="copy-tracking-link-btn"
                    >
                      Copy tracking link
                    </button>
                  </div>
                </div>
              )}
              {!pickerCoords ? (
                // Step 1 — either fetch current GPS, or type an address
                <>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Help your driver reach the exact spot. We'll show your location on a map so you can drag the pin to your gate or building entrance.
                  </p>

                  {/* Address search box (Places Autocomplete).
                      Only renders if BOTH the Maps JS API loaded AND
                      the Places library is available. If Places API is
                      not enabled on the Google Cloud project, the
                      autocomplete would throw the loud "This page can't
                      load Google Maps correctly" alert. We detect that
                      and silently hide the field, letting the customer
                      fall back to the GPS button or pin-drop. */}
                  {mapsLoaded &&
                   process.env.REACT_APP_GOOGLE_MAPS_API_KEY &&
                   typeof window !== 'undefined' &&
                   window.google?.maps?.places && (
                    <div>
                      <label className="block text-xs uppercase tracking-wide text-emerald-400 font-semibold mb-1">
                        Search Address
                      </label>
                      <Autocomplete
                        onLoad={(ac) => { autocompleteRef.current = ac; }}
                        onPlaceChanged={() => {
                          const place = autocompleteRef.current?.getPlace?.();
                          const loc = place?.geometry?.location;
                          if (!loc) return;
                          const next = { lat: loc.lat(), lng: loc.lng() };
                          setPickerCoords(next);
                          setPickerAddress(place.formatted_address || place.name || '');
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Type street, landmark, or building name…"
                          className="w-full px-3 py-2.5 rounded-lg bg-slate-900 border border-slate-700 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500"
                          data-testid="picker-search-input"
                        />
                      </Autocomplete>
                      <p className="mt-1 text-[11px] text-gray-500">
                        Or use the button below to detect your current location.
                      </p>
                    </div>
                  )}

                  <Button
                    onClick={handleShareLocation}
                    disabled={gettingLocation}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 text-base font-semibold rounded-xl"
                    data-testid="share-current-location-btn"
                  >
                    {gettingLocation ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Getting Your Location…
                      </>
                    ) : (
                      <>
                        <MapPin className="w-5 h-5 mr-2" />
                        Use My Current Location
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      // Fallback path — if both Places search AND GPS
                      // are blocked (common in in-app browsers), let
                      // the customer drop a pin manually starting at
                      // a sensible default (the order's existing
                      // delivery coordinates, or Dubai centre).
                      const fallback = trackingData?.destination_coords?.latitude
                        ? { lat: trackingData.destination_coords.latitude,
                            lng: trackingData.destination_coords.longitude }
                        : { lat: 25.2048, lng: 55.2708 };
                      setPickerCoords(fallback);
                      fetchPickerAddress(fallback);
                    }}
                    className="w-full border-slate-600 text-gray-300 hover:bg-slate-700"
                    data-testid="drop-pin-manually-btn"
                  >
                    Or drop the pin manually on the map
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowLocationPicker(false)}
                    className="w-full border-slate-600 text-gray-300 hover:bg-slate-700"
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                // Step 2 — show map with draggable pin
                <>
                  <div className="text-xs text-emerald-400 font-semibold uppercase tracking-wide">
                    Drag the pin to your exact location
                  </div>
                  <div className="h-64 rounded-xl overflow-hidden border border-slate-700">
                    {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
                      <div className="h-full w-full flex items-center justify-center text-xs text-gray-400 bg-slate-900">
                        Map unavailable
                      </div>
                    ) : !mapsLoaded ? (
                      <div className="h-full w-full flex items-center justify-center text-sm text-gray-400 bg-slate-900">
                        Loading map…
                      </div>
                    ) : (
                      <GoogleMap
                        mapContainerStyle={{ width: '100%', height: '100%' }}
                        center={pickerCoords}
                        zoom={17}
                        options={{
                          streetViewControl: false,
                          mapTypeControl: false,
                          fullscreenControl: false,
                          zoomControl: true,
                          clickableIcons: false,
                          gestureHandling: 'greedy',
                        }}
                      >
                        <Marker
                          position={pickerCoords}
                          draggable
                          onDragEnd={(e) => {
                            const next = { lat: e.latLng.lat(), lng: e.latLng.lng() };
                            setPickerCoords(next);
                            fetchPickerAddress(next);
                          }}
                        />
                      </GoogleMap>
                    )}
                  </div>

                  <div className="bg-slate-900/60 rounded-xl border border-slate-700 px-3 py-2.5 text-sm">
                    <div className="flex items-center gap-2 text-gray-400 text-[10px] uppercase tracking-wide mb-1">
                      <MapPin className="w-3 h-3" /> Address
                    </div>
                    <div className="text-white" data-testid="picker-resolved-address">
                      {pickerLoadingAddress ? (
                        <span className="inline-flex items-center gap-2 text-gray-400">
                          <Loader2 className="w-3 h-3 animate-spin" /> Resolving…
                        </span>
                      ) : (
                        pickerAddress || `${pickerCoords.lat.toFixed(6)}, ${pickerCoords.lng.toFixed(6)}`
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setPickerCoords(null);
                        setPickerAddress('');
                      }}
                      className="flex-1 border-slate-600 text-gray-300 hover:bg-slate-700"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                    <Button
                      onClick={confirmPickerLocation}
                      disabled={savingPickerLocation || pickerLoadingAddress}
                      className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
                      data-testid="confirm-location-btn"
                    >
                      {savingPickerLocation ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving…
                        </>
                      ) : (
                        <>Confirm Location</>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>

            <p className="text-[11px] text-gray-500 px-5 pb-5 text-center">
              Your location will only be used for this delivery
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderTrackingPage;
